import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  OutlinedInput,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { loginSchema } from "validations/ValidationsSchema";
import { Field, Form, Formik } from "formik";
import { commonStyle } from "../common.styles";
import theme from "themes/Theme";
import PrimaryButton from "components/PrimaryButton/PrimaryButton";
import { Link, useNavigate } from "react-router-dom";
import * as motion from "motion/react-client";
import { use, useEffect, useState } from "react";
import EyeHiddenIcon from "assets/svg/EyeHiddenIcon";
import EyeVisibleIcon from "assets/svg/EyeVisibleIcon";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "reduxStore/store";
import { saveToStore, StoreType } from "services/localStorage";
import { LOGIN_TYPE } from "reduxStore/actions/authActionType";
import { toast } from "react-toastify";
import { Logo } from "assets/index";
import VerifyTwoFA from "components/VerifyTwoFA/VerifyTwoFA";

const LoginPage = () => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [showTwoFAModal, setShowTwoFAModal] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activateEmail, setActivateEmail] = useState("");
  const { user, loading, error } = useSelector(
    (state: RootState) => state.auth
  );

  const handleTogglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const validate = (values: { email: string; password: string }) => {
    const result = loginSchema.safeParse(values);
    if (!result.success) {
      return result.error.format();
    }
    return {};
  };

  useEffect(() => {
    if (error) {
      toast.error(error);
    } else if (user) {
      if (user?.data?.role && user?.data?.role?.toLowerCase() === "user") {
        if (user.responseCode === 112405) {
          toast.info("Please verify your email address");
          navigate("/auth/verify-email", { state: activateEmail });
        } else {
          if (user?.data?.twoFactorEnabled === 1) {
            saveToStore(user?.data, StoreType.AUTH);
            saveToStore(user?.data?.token, StoreType.AUTH_TOKEN);
            setShowTwoFAModal(true);
          } else {
            handleTwoFA();
          }
          setActivateEmail("");
        }
      } else {
        toast.error("You are not authorized to login");
      }
    }
  }, [user, error]);

  const handleTwoFA = () => {
    saveToStore(user?.data, StoreType.AUTH);
    saveToStore(user?.data?.token, StoreType.AUTH_TOKEN);
    toast.success(user?.message);
    navigate("/dashboard");
  };

  const handleLogin = (values: { email: string; password: string }) => {
    setActivateEmail(values.email);
    dispatch({
      type: LOGIN_TYPE,
      payload: {
        email: values.email,
        password: values.password,
      },
    });
  };

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      transition={{
        type: "spring",
        stiffness: 50,
      }}
      style={{
        width: "80%",
        overflowY: "auto",
      }}
    >
      <Box>
        <center>
          <Box
            component={"img"}
            src={Logo}
            sx={{
              width: "120px",
              height: "120px",
              objectFit: "contain",
              "&:hover": { cursor: "pointer" },
            }}
            onClick={() => {
              navigate("/");
            }}
          />
          <Typography style={commonStyle.titleText} mb={4}>
            Welcome Back!
          </Typography>
        </center>

        <Formik
          initialValues={{ email: "", password: "" }}
          validate={validate}
          onSubmit={(values) => {
            handleLogin(values);
          }}
        >
          {({ handleSubmit }) => (
            <Form
              onSubmit={handleSubmit}
              style={{ display: "flex", flexDirection: "column" }}
            >
              <Typography sx={commonStyle.inputLabelStyle}>
                Email address
              </Typography>
              <Field name="email">
                {({ field, meta }: any) => (
                  <TextField
                    {...field}
                    type="email"
                    margin="dense"
                    size="small"
                    fullWidth
                    variant="outlined"
                    slotProps={commonStyle.inputSlotTextStyle}
                    error={meta.touched && !!meta.error}
                    helperText={
                      meta.touched && meta.error ? meta.error._errors?.[0] : ""
                    }
                  />
                )}
              </Field>

              <Typography sx={commonStyle.inputLabelStyle}>Password</Typography>
              <Field name="password">
                {({ field, meta }: any) => (
                  <TextField
                    {...field}
                    type={isPasswordVisible ? "text" : "password"}
                    margin="dense"
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder="Enter Password"
                    slotProps={{
                      input: {
                        sx: {
                          fontSize: "0.875rem",
                          color: theme.palette.text.primary,
                          fontWeight: 400,
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleTogglePasswordVisibility}
                              aria-label="toggle password visibility"
                            >
                              {isPasswordVisible ? (
                                <EyeHiddenIcon />
                              ) : (
                                <EyeVisibleIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                    slots={{ input: OutlinedInput }}
                    error={meta.touched && Boolean(meta.error)}
                    helperText={
                      meta.touched && meta.error ? meta.error._errors?.[0] : ""
                    }
                  />
                )}
              </Field>
              <Box sx={commonStyle.rightAlignText} mt={2}>
                <Link
                  to={"/auth/forget-password"}
                  style={{
                    ...styles.forgetPasswordText,
                    textDecoration: "none",
                  }}
                >
                  Forget Password?
                </Link>
              </Box>

              {loading ? (
                <CircularProgress sx={{ mt: 6 }} />
              ) : (
                <PrimaryButton
                  title="Login"
                  onClick={handleSubmit}
                  viewStyle={{ width: "100%", mt: 6 }}
                />
              )}
            </Form>
          )}
        </Formik>

        <Divider
          flexItem
          sx={{ borderColor: "rgba(245, 245, 245, 1)", mt: 4, pb: 4 }}
        >
          Or
        </Divider>
        <center>
          <Typography sx={styles.doNotHaveAccount}>
            Don't have account{" "}
            <Link to={"/auth/signup"} style={styles.signupBtn}>
              Sign Up
            </Link>
          </Typography>
        </center>

        {showTwoFAModal ? (
          <VerifyTwoFA
            open={showTwoFAModal}
            handleClose={() => {
              setShowTwoFAModal(false);
            }}
            onSuccess={() => {
              handleTwoFA();
            }}
          />
        ) : null}
      </Box>
    </motion.div>
  );
};
export default LoginPage;
const styles = {
  forgetPasswordText: {
    fontSize: "clamp(0.6rem, 4vw, 0.825rem)",
    color: theme.palette.primary.main,
    "&:hover": {
      cursor: "pointer",
    },
  },
  agreementText: {
    fontSize: "clamp(0.6rem, 4vw, 0.825rem)",
    color: theme.palette.text.primary,
  },
  termsConditionText: {
    color: theme.palette.primary.main,
  },
  doNotHaveAccount: {
    fontSize: "clamp(0.85rem, 4vw, 1rem)",
    color: theme.palette.text.primary,
  },
  signupBtn: {
    color: theme.palette.primary.main,
  },
};
